import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Topbar from '../components/Topbar';
import Spinner from '../components/Spinner';
import { getNotifications, markAsRead, markAllAsRead } from '../services/notificationService';
import { useNotifications } from '../utils/NotificationContext';
import { onNewNotification, onNotificationCount } from '../services/socketService';

const Notifications = () => {
  const navigate = useNavigate();
  const { notificationCount, updateNotificationCount } = useNotifications();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, read, unread
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [counts, setCounts] = useState({ all: 0, unread: 0, read: 0, viewed: 0, unviewed: 0 });
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });

  useEffect(() => {
    fetchNotifications();
  }, [page]);

  // Subscribe to real-time count updates
  useEffect(() => {
    const unsubscribe = onNotificationCount((counts) => {
      if (counts && typeof counts === 'object') {
        setCounts(counts);
        updateNotificationCount(counts);
      }
    });
    return unsubscribe;
  }, [updateNotificationCount]);

  // Subscribe to new notifications real-time
  useEffect(() => {
    const unsubscribe = onNewNotification((notification) => {
      setNotifications(prev => [notification, ...prev]);
      setCounts(prev => ({
        ...prev,
        all: (prev.all || 0) + 1,
        unread: (prev.unread || 0) + 1,
        unviewed: (prev.unviewed || 0) + 1
      }));
    });

    return unsubscribe;
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      // Fetch notifications with socket support
      const response = await getNotifications(10, page);
      console.log('Notifications Page: fetchNotifications response:', response);
      const notificationsArray = Array.isArray(response?.data) ? response.data : [];
      setNotifications(notificationsArray);

      // Extract counts from socket response
      if (response?.meta?.counts) {
        setCounts(response.meta.counts);
        updateNotificationCount(response.meta.counts);
      } else {
        // Fallback if counts not provided
        const newCounts = {
          all: notificationsArray.length,
          unread: notificationsArray.filter(n => !n.read).length,
          read: notificationsArray.filter(n => n.read).length,
          viewed: notificationsArray.filter(n => n.viewed).length,
          unviewed: notificationsArray.filter(n => !n.viewed).length
        };
        setCounts(newCounts);
        updateNotificationCount(newCounts);
      }

      // Extract pagination metadata
      if (response?.meta) {
        setMeta({
          total: response.meta.total_matching_record || response.meta.total || 0,
          totalPages: response.meta.last_page || 1
        });
      }
    } catch (error) {
      console.error('Notifications Page: Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      try {
        await markAsRead(notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
        );
        // Update counts when marking a notification as read
        const newCounts = {
          ...counts,
          unread: Math.max(0, counts.unread - 1),
          read: counts.read + 1
        };
        setCounts(newCounts);
        updateNotificationCount(newCounts);
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    // Extract POI ID from various possible fields
    const poiId =
      notification.poi_id ||
      notification.poiId ||
      notification.data?.poi_id ||
      notification.data?.poiId ||
      notification.poi?.id;

    // Always navigate to POI detail page
    if (poiId) {
      navigate(`/pois?view=${poiId}`);
    } else {
      navigate('/pois');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      // Update counts after marking all as read
      const newCounts = {
        ...counts,
        unread: 0,
        read: counts.all
      };
      setCounts(newCounts);
      updateNotificationCount(newCounts);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getFilteredNotifications = () => {
    let filtered = notifications;

    if (filter === 'read') {
      filtered = notifications.filter(n => n.read);
    } else if (filter === 'unread') {
      filtered = notifications.filter(n => !n.read);
    }

    return filtered;
  };

  const getTotalPagesForFilter = () => {
    if (filter === 'all') {
      return meta.totalPages;
    } else if (filter === 'unread') {
      return Math.ceil(counts.unread / limit);
    } else if (filter === 'read') {
      return Math.ceil(counts.read / limit);
    }
    return 1;
  };

  const getNotificationIcon = (type) => {
    const icons = {
      order: '🛒',
      alert: '⚠️',
      user: '👤',
      update: '📡',
      success: '✅',
      security: '🔒',
      payment: '💰',
      maintenance: '🔧',
      report: '📊',
      approval: '✋'
    };
    return icons[type] || '🔔';
  };

  const getNotificationColor = (type) => {
    const colors = {
      order: { bg: '#EEF6FF', border: '#B3D9FF', text: '#66B2FF' },
      alert: { bg: '#FFF0F0', border: '#FFD5D5', text: '#FF5C5C' },
      user: { bg: '#F5F0FF', border: '#D8BFD8', text: '#8B5CF6' },
      update: { bg: '#FFF4E0', border: '#F5CBA7', text: '#F59E0B' },
      success: { bg: '#EAFAF3', border: '#A8DDB5', text: '#1A7A42' },
      security: { bg: '#FFF0F0', border: '#FFD5D5', text: '#FF5C5C' },
      payment: { bg: '#EAFAF3', border: '#A8DDB5', text: '#1A7A42' },
      maintenance: { bg: '#FFF4E0', border: '#F5CBA7', text: '#F59E0B' },
      report: { bg: '#EEF6FF', border: '#B3D9FF', text: '#66B2FF' },
      approval: { bg: '#F5F0FF', border: '#D8BFD8', text: '#8B5CF6' }
    };
    return colors[type] || { bg: '#EEF6FF', border: '#B3D9FF', text: '#66B2FF' };
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filteredNotifications = getFilteredNotifications();
  const totalPages = getTotalPagesForFilter();

  if (loading) {
    return <Spinner />;
  }

  return (
    <div>
      <Topbar title="Notifications" subtitle="All your notifications in one place" />

      <div className="content">
        {/* Header with filters */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, fontWeight: 600, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
              Filter Notifications
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { setFilter('all'); setPage(1); }}
                style={{
                  padding: '8px 16px',
                  borderRadius: 10,
                  border: 'none',
                  background: filter === 'all' ? 'var(--navy)' : 'white',
                  color: filter === 'all' ? 'white' : 'var(--dark)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  borderColor: filter === 'all' ? 'var(--navy)' : '#E8EDF3',
                  borderWidth: filter === 'all' ? 0 : 1,
                  transition: 'all 0.2s'
                }}
              >
                All ({counts.all})
              </button>
              <button
                onClick={() => { setFilter('unread'); setPage(1); }}
                style={{
                  padding: '8px 16px',
                  borderRadius: 10,
                  border: `1px solid ${filter === 'unread' ? 'var(--navy)' : '#E8EDF3'}`,
                  background: filter === 'unread' ? 'var(--navy)' : 'white',
                  color: filter === 'unread' ? 'white' : 'var(--dark)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Unread ({counts.unread})
              </button>
              <button
                onClick={() => { setFilter('read'); setPage(1); }}
                style={{
                  padding: '8px 16px',
                  borderRadius: 10,
                  border: `1px solid ${filter === 'read' ? 'var(--navy)' : '#E8EDF3'}`,
                  background: filter === 'read' ? 'var(--navy)' : 'white',
                  color: filter === 'read' ? 'white' : 'var(--dark)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Read ({counts.read})
              </button>
            </div>
          </div>
          {counts.unread > 0 && (
            <button
              onClick={handleMarkAllRead}
              style={{
                padding: '10px 18px',
                borderRadius: 10,
                border: 'none',
                background: '#EEF6FF',
                color: '#66B2FF',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Mark all as read
            </button>
          )}
        </div>

        {/* Notifications List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filteredNotifications.length === 0 ? (
            <div
              style={{
                background: 'white',
                borderRadius: 16,
                border: '1px solid #E8EDF3',
                padding: '60px 40px',
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: 48, marginBottom: 16 }}>🔔</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>
                {filter === 'all' ? 'No notifications' : `No ${filter} notifications`}
              </div>
              <div style={{ fontSize: 14, color: 'var(--grey)' }}>
                {filter === 'all' ? 'You are all caught up!' : 'Check back later for updates'}
              </div>
            </div>
          ) : (
            filteredNotifications.map((notification) => {
              const colors = getNotificationColor(notification.type);
              return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  style={{
                    background: notification.read ? 'white' : '#F8FBFF',
                    borderRadius: 14,
                    border: `1.5px solid ${notification.read ? '#E8EDF3' : '#66B2FF'}`,
                    padding: '18px 20px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    gap: 16,
                    ':hover': { borderColor: '#66B2FF', background: '#F5F7FA' }
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#66B2FF';
                    e.currentTarget.style.background = '#F5F7FA';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = notification.read ? '#E8EDF3' : '#66B2FF';
                    e.currentTarget.style.background = notification.read ? 'white' : '#F8FBFF';
                  }}
                >
                  {/* Icon Container */}
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: colors.bg,
                      border: `1px solid ${colors.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 20,
                      flexShrink: 0
                    }}
                  >
                    {getNotificationIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 14,
                          color: 'var(--navy)',
                          fontFamily: "'Space Grotesk', sans-serif"
                        }}
                      >
                        {notification.title}
                      </div>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '3px 10px',
                          borderRadius: 6,
                          fontSize: 10,
                          fontWeight: 700,
                          background: colors.bg,
                          color: colors.text,
                          textTransform: 'uppercase'
                        }}
                      >
                        {notification.type}
                      </div>
                      {!notification.read && (
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: '#66B2FF',
                            marginLeft: 'auto'
                          }}
                        />
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--dark)', marginBottom: 6, lineHeight: 1.4 }}>
                      {notification.message}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--grey)', display: 'flex', gap: 12 }}>
                      <span>{formatTime(notification.createdAt)}</span>
                      {!notification.read && (
                        <span style={{ fontWeight: 600, color: 'var(--blue)' }}>Unread</span>
                      )}
                    </div>
                  </div>

                  {/* Status indicator */}
                  {notification.read && (
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--grey)',
                        padding: '4px 10px',
                        background: '#F0F4F8',
                        borderRadius: 6,
                        height: 'fit-content',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Read
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {filteredNotifications.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, paddingTop: 20, borderTop: '1px solid #E8EDF3' }}>
            <div style={{ fontSize: 13, color: 'var(--grey)' }}>
              Showing <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{(page - 1) * limit + 1}</span> to <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{Math.min(page * limit, (filter === 'all' ? counts.all : (filter === 'unread' ? counts.unread : counts.read)))}</span> of <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{filter === 'all' ? counts.all : (filter === 'unread' ? counts.unread : counts.read)}</span> notifications
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #E8EDF3',
                  background: page === 1 ? '#F0F4F8' : 'white',
                  color: page === 1 ? 'var(--grey)' : 'var(--navy)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                ← Previous
              </button>

              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setPage(i + 1)}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 6,
                      border: page === i + 1 ? 'none' : '1px solid #E8EDF3',
                      background: page === i + 1 ? 'var(--navy)' : 'white',
                      color: page === i + 1 ? 'white' : 'var(--navy)',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #E8EDF3',
                  background: page === totalPages ? '#F0F4F8' : 'white',
                  color: page === totalPages ? 'var(--grey)' : 'var(--navy)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: page === totalPages ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;

